import { JwtPayload } from '@aws-amplify/core/internals/utils';
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Tr,
  VStack,
} from '@chakra-ui/react';
import auth from '@common/auth';
import config from '@common/config';
import session from '@common/session';
import { Domicile } from '@services/appsync/types';
import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextBlock } from '../../common/components/TextBlock';
import { ROUTES } from '../../config';
import { useCreateAccountViewModel } from '../models/CreateAccountViewModel';
import { CreateAccountForm } from '../types';
import { PageHeader } from './components/PageHeader';
import { UI } from './components/UI';

type AccountReviewProps = {
  form: UseFormReturn<CreateAccountForm>;
};

export const AccountReview: React.FC<AccountReviewProps> = ({ form }) => {
  const [concreitAgreementAccepted, setConcreitAgreementAccepted] = useState(false);
  const [dwollaAgreementAccepted, setDwollaAgreementAccepted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState<JwtPayload | null>(null);
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const redirectAsParam = redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : '';
  const redirectUrl = redirectParam ? decodeURIComponent(redirectParam) : ROUTES.dashboard;
  const model = useCreateAccountViewModel();
  const formValues = form.getValues();
  const formValid =
    formValues.address1 && formValues.city && formValues.state && formValues.zip && formValues.citizenship;
  const displayName = user?.given_name
    ? `${user?.given_name} ${user?.family_name}`
    : (user?.name as string | undefined);
  const navigate = useNavigate();

  useEffect(() => {
    const go = async () => {
      const result = await auth.getCurrentSession();
      setUser(result.tokens?.idToken?.payload || null);
    };
    go();
  }, []);

  const onSubmit = async () => {
    if (!form || !user) return;
    setProcessing(true);
    const status = await model.createInvestorAccount(form);
    if (status.success && status.investorId) {
      await session.start(status.investorId);
      navigate(redirectUrl);
      setProcessing(false);
    }
  };

  return (
    <UI prevPage={ROUTES.createAddress + redirectAsParam}>
      <VStack direction={'column'} gap="24px" align="center">
        <VStack maxW={'sm'}>
          <PageHeader title="Account Review" subtitle="Please confirm that the following information is correct" />
        </VStack>
        <VStack maxW={'lg'} justifyContent={'center'} alignItems={'center'} gap="20px">
          <TableContainer>
            <Table>
              <Tbody>
                {displayName && (
                  <Tr>
                    <Th paddingX={0}>Name</Th>
                    <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                      {displayName}
                    </Td>
                  </Tr>
                )}
                <Tr>
                  <Th paddingX={0}>Email</Th>
                  <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                    {user?.email as string | undefined}
                  </Td>
                </Tr>
                <Tr>
                  <Th paddingX={0}>Phone</Th>
                  <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                    {user?.phone_number as string | undefined}
                  </Td>
                </Tr>
                <Tr>
                  <Th paddingX={0}>Status</Th>
                  <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                    {`${formValues.citizenship === Domicile.USR ? 'US Resident' : 'US Citizen'}`}
                  </Td>
                </Tr>
                <Tr>
                  <Th paddingX={0}>Address</Th>
                  <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                    <TextBlock>{formValues.address1 || ''}</TextBlock>
                    <TextBlock>{`${formValues.city || ''}, ${formValues.state || ''} ${
                      formValues.zip || ''
                    }`}</TextBlock>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        </VStack>
        <VStack maxW={'sm'} fontSize="14px" gap="20px">
          <Box>
            <HStack alignItems={'flex-start'} gap="10px">
              <Checkbox
                size={'lg'}
                isChecked={concreitAgreementAccepted}
                onChange={(e) => {
                  e.stopPropagation();
                  setConcreitAgreementAccepted(!concreitAgreementAccepted);
                }}
                mt={'4px'}></Checkbox>
              <TextBlock
                onClick={(e) => {
                  e.stopPropagation();

                  setConcreitAgreementAccepted(!concreitAgreementAccepted);
                }}>
                By checking this box, you agree to{' '}
                <Link
                  href={config.TERMS}
                  target={'_blank'}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}>
                  Concreit's Terms of Use
                </Link>
                {' and '}
                <Link
                  href={config.PRIVACY_POLICY}
                  target={'_blank'}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}>
                  Privacy Policy
                </Link>
                {' and our Partner '}
                <Link
                  href={config.DWOLLA_TERMS_OF_SERVICE}
                  target={'_blank'}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}>
                  Dwolla's Terms of Service
                </Link>
                {' and '}
                <Link
                  href={config.DWOLLA_PRIVACY_POLICY}
                  target={'_blank'}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}>
                  Privacy Policy
                </Link>
              </TextBlock>
            </HStack>
          </Box>
          <Box>
            <HStack alignItems={'flex-start'} gap="10px">
              <Checkbox
                size={'lg'}
                isChecked={dwollaAgreementAccepted}
                onChange={(e) => {
                  e.stopPropagation();
                  setDwollaAgreementAccepted(!dwollaAgreementAccepted);
                }}
                mt={'4px'}></Checkbox>
              <TextBlock
                onClick={(e) => {
                  e.stopPropagation();

                  setDwollaAgreementAccepted(!dwollaAgreementAccepted);
                }}>
                By checking this box, you agree to our{' '}
                <Link
                  href={config.PROGRAM_AGREEMENT}
                  target={'_blank'}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}>
                  Program Agreement
                </Link>
                {' and '}
                <Link
                  href={config.ESIGN}
                  target={'_blank'}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}>
                  E-sign Consent
                </Link>
              </TextBlock>
            </HStack>
          </Box>
          <Button
            type="button"
            isLoading={processing}
            w={['full', 'auto']}
            p={7}
            onClick={() => onSubmit()}
            isDisabled={!concreitAgreementAccepted || !dwollaAgreementAccepted || !formValid}>
            Create Investor Profile
          </Button>
        </VStack>
      </VStack>
    </UI>
  );
};
